import type { AdminAreaId, AdminDashboardRouteState } from "./admin-dashboard-route-state"
import type { ResolvedManagementSectionEntry } from "./management-section-registry"
import type { CapabilityMode } from "@/api/admin/delegation"

import {
  Activity,
  BarChart3,
  Bell,
  Coins,
  FileText,
  Gauge,
  GraduationCap,
  HeartPulse,
  LockKeyhole,
  type LucideIcon,
  MessageSquare,
  Settings,
  TrendingUp,
  Users,
  Wrench,
} from "@/components/icons"

import { CATEGORY_LABEL_DEFAULTS, CATEGORY_LABEL_KEYS } from "./management-category-labels"

/** An i18n key paired with the English string to fall back on. */
type Labelled = {
  labelKey: string
  labelFallback: string
}

export type AdminAreaEntry = Labelled & {
  id: AdminAreaId
  icon: LucideIcon
  /**
   * The `core/admin_capabilities.py` key that decides whether a customer
   * admin sees the area at all. Areas without one are always visible.
   */
  capability?: string
}

/**
 * The six top-level admin areas, in tab order.
 *
 * Single source of truth for both the area tab strip and the global search
 * palette's group headings.
 */
export const ADMIN_AREA_ENTRIES: AdminAreaEntry[] = [
  {
    id: "overview",
    icon: Activity,
    labelKey: "adminDashboard.workspaceNavigation.areas.overview",
    labelFallback: "Overview",
  },
  {
    id: "usage",
    icon: BarChart3,
    labelKey: "adminDashboard.workspaceNavigation.areas.usageBilling",
    labelFallback: "Usage & billing",
  },
  {
    id: "operations",
    icon: Wrench,
    labelKey: "adminDashboard.workspaceNavigation.areas.operations",
    labelFallback: "Operations",
  },
  {
    id: "settings",
    icon: LockKeyhole,
    labelKey: "adminDashboard.workspaceNavigation.areas.settings",
    labelFallback: "Settings",
  },
]

export type AdminViewEntry = Labelled & {
  id: string
  icon: LucideIcon
  searchTerms?: string[]
  /** Only offered when the tender route is enabled. */
  requiresTenderRoute?: boolean
}

/** Usage & cost sub-views, in tab order. */
export const ADMIN_USAGE_VIEW_ENTRIES: AdminViewEntry[] = [
  {
    id: "activity",
    icon: Activity,
    labelKey: "adminDashboard.workspaceNavigation.usage.activity",
    labelFallback: "Activity",
    searchTerms: ["activity", "usage", "requests", "insights"],
  },
  {
    id: "adoption",
    icon: TrendingUp,
    labelKey: "adminDashboard.workspaceNavigation.usage.adoption",
    labelFallback: "Adoption",
    searchTerms: ["adoption", "engagement", "active users"],
  },
  {
    id: "costs",
    icon: Coins,
    labelKey: "adminDashboard.workspaceNavigation.usage.costs",
    labelFallback: "Costs & quotas",
    // Every fair-use word — "fair use", "ai budget", "budget", "requests",
    // "plans", "limits", "fallback model" — belongs to the Fair use area now
    // that the ops card moved there with the rest of them. A search for one of
    // them must not offer two destinations for the same control, so this view
    // keeps only the words that describe Azure spend itself.
    searchTerms: ["cost", "costs", "quota", "quotas", "spend", "tokens"],
  },
  {
    id: "training",
    icon: GraduationCap,
    labelKey: "adminDashboard.workspaceNavigation.usage.training",
    labelFallback: "Training & users",
    searchTerms: ["training", "users", "top users", "completion"],
  },
  {
    id: "feedback",
    icon: MessageSquare,
    labelKey: "adminDashboard.workspaceNavigation.usage.feedback",
    labelFallback: "Feedback",
    searchTerms: ["feedback", "ratings", "thumbs"],
  },
  {
    id: "fair-use-drift",
    icon: Gauge,
    labelKey: "adminDashboard.workspaceNavigation.fairUse.budgetActivity",
    labelFallback: "Budget activity",
    searchTerms: ["fair use", "ai budget", "budget", "requests"],
  },
  {
    id: "fair-use-users",
    icon: Users,
    labelKey: "adminDashboard.workspaceNavigation.fairUse.users",
    labelFallback: "Users & licences",
    searchTerms: ["user plan", "licence", "licences"],
  },
  {
    id: "fair-use-leads",
    icon: Users,
    labelKey: "adminDashboard.workspaceNavigation.fairUse.leads",
    labelFallback: "Local AI Leads",
    searchTerms: ["local ai lead", "ambassador", "lead group", "allowance"],
  },
  {
    id: "fair-use-costs",
    icon: Coins,
    labelKey: "adminDashboard.workspaceNavigation.fairUse.costAssumptions",
    labelFallback: "Cost assumptions",
    searchTerms: ["fixed cost", "infrastructure cost", "seat benchmark", "lead dashboard"],
  },
  {
    id: "fair-use-plans",
    icon: Coins,
    labelKey: "adminDashboard.workspaceNavigation.fairUse.plans",
    labelFallback: "Plans & pricing",
    searchTerms: ["pricing", "prices", "top-up"],
  },
  {
    id: "fair-use-settings",
    icon: Settings,
    labelKey: "adminDashboard.workspaceNavigation.fairUse.budgetPolicy",
    labelFallback: "Budget policy",
    searchTerms: ["limits", "fallback model", "threshold", "delegation"],
  },
]

/** Operations sub-views, in tab order. */
export const ADMIN_OPERATIONS_VIEW_ENTRIES: AdminViewEntry[] = [
  {
    id: "health",
    icon: HeartPulse,
    labelKey: "adminDashboard.workspaceNavigation.operations.health",
    labelFallback: "System health",
    searchTerms: ["health", "status", "uptime", "diagnostics"],
  },
  {
    id: "alerts",
    icon: Bell,
    labelKey: "adminDashboard.workspaceNavigation.operations.alerts",
    labelFallback: "Alerts",
    searchTerms: ["alerts", "events", "incidents"],
  },
  {
    id: "tenders",
    icon: FileText,
    labelKey: "adminDashboard.workspaceNavigation.operations.tenders",
    labelFallback: "Tenders",
    searchTerms: ["tender", "tenders", "cases", "runs"],
    requiresTenderRoute: true,
  },
]

export type AdminSearchEntryKind = "area" | "view" | "section"

export type AdminSearchEntry = Labelled & {
  /** Stable and unique across kinds — used as the React key and cmdk value. */
  key: string
  kind: AdminSearchEntryKind
  icon: LucideIcon
  /** Group heading: the admin area this destination lives in. */
  groupKey: string
  groupFallback: string
  /** Secondary line under the label (category for sections, area for the rest). */
  subtitleKey: string
  subtitleFallback: string
  target: AdminDashboardRouteState
  searchTerms?: string[]
}

function areaEntry(area: AdminAreaId): AdminAreaEntry {
  const found = ADMIN_AREA_ENTRIES.find((entry) => entry.id === area)
  // ADMIN_AREA_ENTRIES covers every AdminAreaId, so this is unreachable in
  // practice; the fallback only keeps the return type non-nullable.
  return found ?? ADMIN_AREA_ENTRIES[0]
}

function viewEntries(
  entries: AdminViewEntry[],
  area: AdminAreaId,
  hasTenderRoute: boolean
): AdminSearchEntry[] {
  const group = areaEntry(area)
  return entries
    .filter((entry) => !entry.requiresTenderRoute || hasTenderRoute)
    .map((entry) => ({
      key: `view:${area}:${entry.id}`,
      kind: "view" as const,
      icon: entry.icon,
      labelKey: entry.labelKey,
      labelFallback: entry.labelFallback,
      groupKey: group.labelKey,
      groupFallback: group.labelFallback,
      subtitleKey: group.labelKey,
      subtitleFallback: group.labelFallback,
      target: { area, view: entry.id },
      searchTerms: [entry.id, ...(entry.searchTerms ?? [])],
    }))
}

/**
 * Four shared areas are currently visible to every admin. Keep the existing
 * capability filter for any future area that carries a capability; Fair use
 * itself is now a nested Usage & billing task filtered by `fairUseVisible`.
 */
export function getVisibleAdminAreas(
  capabilityModes: Record<string, string> | undefined,
  options: {
    isPlatformAdmin?: boolean
    previewModes?: Record<string, CapabilityMode>
    keepAreaIds?: readonly AdminAreaId[]
  } = {}
): AdminAreaEntry[] {
  const { isPlatformAdmin = false, previewModes, keepAreaIds } = options
  if (!previewModes && isPlatformAdmin) return ADMIN_AREA_ENTRIES

  const modes = previewModes ?? capabilityModes
  return ADMIN_AREA_ENTRIES.filter((area) => {
    if (!area.capability) return true
    if (keepAreaIds?.includes(area.id)) return true
    const mode = modes?.[area.capability]
    return mode === "manage" || mode === "available"
  })
}

/**
 * One capability's mode, resolved the way {@link getVisibleAdminAreas}
 * resolves it — the previewed customer's matrix while a platform admin is
 * previewing, the admin's own `/api/config` map otherwise.
 *
 * An AREA that renders its own body needs this as well as the nav list: the
 * nav can keep a tab while the area behind it re-derives "platform admin,
 * everything" from `/api/config` and renders the full editable surface to a
 * preview that asked for a customer's view.
 */
export function resolveAreaCapabilityMode(
  capability: string,
  capabilityModes: Record<string, string> | undefined,
  options: { previewModes?: Record<string, CapabilityMode> } = {}
): CapabilityMode | undefined {
  const modes = options.previewModes ?? capabilityModes
  const mode = modes?.[capability]
  return mode === "manage" || mode === "available" || mode === "hidden" ? mode : undefined
}

/**
 * Flatten every admin destination — areas, sub-views and management sections —
 * into one searchable list.
 *
 * `sections` must already be capability-filtered by the caller (pass the same
 * `getManagementSections(...)` result the page renders from) so the palette can
 * never surface a section the workspace itself would hide. `areas` plays the
 * same role for whole areas: pass the `getVisibleAdminAreas(...)` result the
 * navigation renders from, and neither the area nor its views are offered.
 */
export function buildAdminSearchEntries(
  sections: ResolvedManagementSectionEntry[],
  hasTenderRoute: boolean,
  options: { visibleAreas?: AdminAreaEntry[]; fairUseVisible?: boolean } = {}
): AdminSearchEntry[] {
  const { visibleAreas = ADMIN_AREA_ENTRIES, fairUseVisible = true } = options
  const areas: AdminSearchEntry[] = visibleAreas.map((area) => ({
    key: `area:${area.id}`,
    kind: "area" as const,
    icon: area.icon,
    labelKey: area.labelKey,
    labelFallback: area.labelFallback,
    groupKey: "adminDashboard.globalSearch.groups.areas",
    groupFallback: "Areas",
    subtitleKey: "adminDashboard.globalSearch.groups.areas",
    subtitleFallback: "Areas",
    target: { area: area.id },
    searchTerms: [area.id],
  }))

  const sectionEntries: AdminSearchEntry[] = sections.map((section) => {
    const area: AdminAreaId = "settings"
    const group = areaEntry(area)
    const category = section.taskArea === "access-audit" ? "access-audit" : section.category
    return {
      key: `section:${section.id}`,
      kind: "section" as const,
      icon: section.icon,
      labelKey: section.titleKey,
      labelFallback: section.titleFallback ?? section.id,
      groupKey: group.labelKey,
      groupFallback: group.labelFallback,
      subtitleKey: category ? CATEGORY_LABEL_KEYS[category] : group.labelKey,
      subtitleFallback: category ? CATEGORY_LABEL_DEFAULTS[category] : group.labelFallback,
      target: { area, setting: section.id },
      searchTerms: [section.id, ...(category ? [category] : []), ...(section.searchTerms ?? [])],
    }
  })

  return [
    ...areas,
    ...viewEntries(
      ADMIN_USAGE_VIEW_ENTRIES.filter(
        (entry) => fairUseVisible || !entry.id.startsWith("fair-use-")
      ),
      "usage",
      hasTenderRoute
    ),
    ...viewEntries(ADMIN_OPERATIONS_VIEW_ENTRIES, "operations", hasTenderRoute),
    ...sectionEntries,
  ]
}

/**
 * Case-insensitive substring match over the translated label plus the entry's
 * (English) search terms, mirroring `searchManagementSections`.
 *
 * Matching both means a non-English user can find a destination by its
 * translated name *or* by the English term they know it under — the
 * `searchTerms` in the registries are deliberately English-only.
 */
export function searchAdminEntries(
  entries: AdminSearchEntry[],
  query: string,
  getLabel: (entry: AdminSearchEntry) => string
): AdminSearchEntry[] {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return entries

  return entries.filter((entry) => {
    // `key` is deliberately absent: it is a kind-prefixed identity
    // (`view:usage:costs`) for React/cmdk only, and searching it would make
    // "view", "area" or "section" match every row of that kind. Bare ids are
    // carried in `searchTerms` instead.
    const haystack = [getLabel(entry), ...(entry.searchTerms ?? [])].join(" ").toLocaleLowerCase()

    return haystack.includes(normalized)
  })
}
